import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { fetchAuditExportRows } from './exportAudit';
import { AlvaraRegistrationPanel } from './components/AlvaraRegistrationPanel';
import { useAuditSummary, useAuditDivergences, useAuditNotFound, useAuditFilters, useAuditStatusMismatches } from './hooks';
import { AuditKpiCards, SeverityDonut, FieldHeatmap, DivergenceTable, StatusMismatchTable, NxtCrossSummary } from './components';
import type { AuditSeverity, TaxUnit } from '@claro/shared';

export function AuditoriaTab() {
  const [exportLoading, setExportLoading] = useState(false);
  const summaryQuery = useAuditSummary();
  const divergencesQuery = useAuditDivergences();
  const notFoundQuery = useAuditNotFound();
  const mismatchesQuery = useAuditStatusMismatches();
  const { data: summary, isLoading: loadingSummary } = summaryQuery;
  const { data: divergences, isLoading: loadingDivergences } = divergencesQuery;
  const { data: notFound, isLoading: loadingNotFound } = notFoundQuery;
  const { data: statusMismatches, isLoading: loadingMismatches } = mismatchesQuery;
  const failedQueries = [summaryQuery, divergencesQuery, notFoundQuery, mismatchesQuery].filter(query => query.isError);
  const { toggleSeverity, setField, getFilterParams } = useAuditFilters();

  const handleSliceClick = (severity: AuditSeverity) => {
    toggleSeverity(severity);
  };

  const handleFieldClick = (field: string) => {
    setField(field);
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const params = getFilterParams();

      const [divergencesResponse, notFoundResponse] = await Promise.all([
        fetchAuditExportRows(params),
        apiFetch<{ data: TaxUnit[]; total: number }>(`/claro/api/audit/not-found`),
      ]);

      const divergenceRows = divergencesResponse.map((unit) => ({
        Unidade: unit.unitName || unit.unitId,
        Identificação: unit.unitId,
        CNPJ: unit.cnpj,
        Regional: unit.regional || '—',
        'Identificação Tributária': unit.identificacaoTax || '—',
        Método: unit.matchMethod || '—',
        Severidade: unit.overallSeverity,
        'Campos divergentes': unit.divergingFields,
        'Total de campos': unit.totalFields,
        'Campos detalhados': unit.divergences
          .filter((d) => d.severity > 0)
          .map((d) => `${d.fieldLabel}: ${d.severity}`)
          .join(' | '),
      }));

      const notFoundRows = (notFoundResponse.data || []).map((unit) => ({
        CNPJ: unit.cnpj,
        Identificação: unit.identificacao,
        UF: unit.uf,
        Município: unit.municipio,
      }));

      const workbook = XLSX.utils.book_new();

      const divergenceSheet = XLSX.utils.json_to_sheet(divergenceRows);
      divergenceSheet['!cols'] = [
        { wch: 28 }, { wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 16 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 60 },
      ];
      XLSX.utils.book_append_sheet(workbook, divergenceSheet, 'Divergências');

      const notFoundSheet = XLSX.utils.json_to_sheet(notFoundRows);
      notFoundSheet['!cols'] = [
        { wch: 18 }, { wch: 24 }, { wch: 8 }, { wch: 24 },
      ];
      XLSX.utils.book_append_sheet(workbook, notFoundSheet, 'CNPJs não encontrados');

      XLSX.writeFile(workbook, `auditoria-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar auditoria:', error);
      alert('Erro ao exportar relatório Excel. Tente novamente.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {failedQueries.length > 0 && <div role="alert" className="rounded-lg border border-destructive p-4 text-sm">
        Não foi possível carregar parte da auditoria. Os resultados podem estar incompletos.
        <Button variant="outline" className="ml-3" onClick={() => failedQueries.forEach(query => void query.refetch())}>Tentar novamente</Button>
      </div>}
      {/* KPI Cards */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <AuditKpiCards summary={summary} isLoading={loadingSummary} />
        </div>
        <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exportLoading} className="self-start">
          {exportLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          {exportLoading ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>

      {/* Charts row */}
      <div className="grid gap-4 lg:grid-cols-2">
        <SeverityDonut
          summary={summary}
          isLoading={loadingSummary}
          onSliceClick={handleSliceClick}
        />
        <FieldHeatmap
          summary={summary}
          isLoading={loadingSummary}
          onFieldClick={handleFieldClick}
        />
      </div>

      {/* Divergence table */}
      <DivergenceTable data={divergences} isLoading={loadingDivergences} />

      {/* Not found summary */}
      {!loadingNotFound && notFound && notFound.length > 0 && (
        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
            CNPJs não encontrados no PropertyDocs ({notFound.length})
          </h3>
          <div className="max-h-48 overflow-y-auto text-xs">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-1 pr-2">CNPJ</th>
                  <th className="pb-1 pr-2">Identificação</th>
                  <th className="pb-1 pr-2">UF</th>
                  <th className="pb-1">Município</th>
                </tr>
              </thead>
              <tbody>
                {notFound.map((u) => (
                  <tr key={u.cnpj} className="border-b border-muted/50">
                    <td className="py-1 pr-2 tabular-nums">
                      {u.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
                    </td>
                    <td className="py-1 pr-2">{u.identificacao}</td>
                    <td className="py-1 pr-2">{u.uf}</td>
                    <td className="py-1">{u.municipio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status mismatch: ativa PD × baixada tributário */}
      <StatusMismatchTable data={statusMismatches} isLoading={loadingMismatches} />

      {/* Claro NXT: ausentes, divergentes e ambíguos no cruzamento por CNPJ */}
      <AlvaraRegistrationPanel />
      <NxtCrossSummary />
    </div>
  );
}
